import { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import { http } from '../../features/jobs/api/http';
import { typedLength } from '../../helpers/liveMachineDrums.js';

// Finishing the drum that is on the machine, by hand.
//
// The opposite of cancelling a drum. Cancel says it was never made; this says it
// WAS made and the line never noticed it finishing — the pinoles opened, the
// bobbin came off, and no sensor said so, so the drum stayed open and the queue
// stayed put. On skip2250 that left a drum open across a 31 h stop and the next
// 2 294 m were counted onto it.
//
// It does not complete anything from here. The WORKER owns the active drum: it
// holds the operator spans it has been accumulating, the speed samples, the run
// time, and the profile's counter reset that hands the overshoot on as carry.
// Completing from the browser would write a drum with no operators, no speed and
// no carry, and nothing on screen would say so. So this leaves a request and the
// worker runs the same completion a detected drum end runs — within a loop, a
// second or two, not instantly.
const fmt2 = v => (v == null ? '-' : Number(v).toFixed(2));

const ForceCompleteModal = ({ show, onHide, target }) => {
  const [length, setLength] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!show) return;
    setLength('');
    setReason('');
    setPreview(null);
    setErr('');
    setDone('');
  }, [show, target?.tokenId]);

  // Asked for on open: the counter is the honest default, and it is the one
  // number the browser cannot work out for itself.
  useEffect(() => {
    if (!show || !target?.machineId) return;
    let alive = true;
    http
      .post(`/api/redis/queues/${target.machineId}/force-complete`, {
        tokenId: target.tokenId,
        dryRun: true
      })
      .then(({ data }) => {
        if (alive) setPreview(data);
      })
      .catch(e => {
        if (alive) setErr(e?.response?.data?.error || e?.message || 'Could not read the machine.');
      });
    return () => {
      alive = false;
    };
  }, [show, target?.machineId, target?.tokenId]);

  const typed = typedLength(length);
  const willBe = Number.isFinite(typed) ? typed : preview?.counter ?? null;

  const submit = async () => {
    setErr('');
    setBusy(true);
    try {
      const { data } = await http.post(
        `/api/redis/queues/${target.machineId}/force-complete`,
        {
          tokenId: target.tokenId,
          ...(Number.isFinite(typed) ? { length: typed } : {}),
          reason
        }
      );
      setDone(data.message || 'Requested.');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!target) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">Finish this drum now</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="fs-9 text-700 mb-3">
          <b>{preview?.productLabel || target.productLabel}</b> ·{' '}
          <span className="font-monospace">{target.tokenId}</span> on{' '}
          {String(target.machineId).toUpperCase()}
          <br />
          {preview ? (
            <>
              the counter reads <b>{fmt2(preview.counter)} m</b>, the drum is planned
              for {fmt2(preview.plannedLength)} m
            </>
          ) : (
            <>planned {fmt2(target.planned)} m</>
          )}
        </div>

        <Form.Group className="mb-3" style={{ maxWidth: 220 }}>
          <Form.Label className="fs-9 fw-semibold">
            Metres on the drum
          </Form.Label>
          <Form.Control
            type="text"
            inputMode="decimal"
            placeholder={preview?.counter != null ? fmt2(preview.counter) : ''}
            value={length}
            isInvalid={length !== '' && !Number.isFinite(typed)}
            onChange={e => setLength(e.target.value)}
          />
          <Form.Text className="fs-10">
            Leave it empty to take the counter. Type a number if what came off the
            machine was measured and differs.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fs-9 fw-semibold">
            Why — this goes into the project&apos;s activity log
          </Form.Label>
          <Form.Control
            size="sm"
            value={reason}
            placeholder="e.g. pinoles opened, the drum end was never detected"
            onChange={e => setReason(e.target.value)}
          />
        </Form.Group>

        <Alert variant="info" className="py-2 fs-10 mb-0">
          The drum closes at <b>{willBe == null ? 'the counter' : `${fmt2(willBe)} m`}</b>,
          its operators and run time are recorded as they stand, anything past that
          length is handed on as carry, and{' '}
          {String(target.machineId).toUpperCase()} moves to the next entry in the
          queue.
          <div className="mt-1 text-700">
            The line does it, not this screen — so it happens on the machine&apos;s
            next loop, a second or two from now.
          </div>
        </Alert>

        {err && (
          <Alert variant="danger" className="py-2 fs-9 mt-3 mb-0">
            {err}
          </Alert>
        )}
        {done && (
          <Alert variant="success" className="py-2 fs-9 mt-3 mb-0">
            {done}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="phoenix-secondary" onClick={onHide} disabled={busy}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="success"
          disabled={busy || !!done || (length !== '' && !Number.isFinite(typed))}
          onClick={submit}
        >
          {busy ? <Spinner animation="border" size="sm" /> : 'Finish the drum'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ForceCompleteModal;
